import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import PreviewSendCertificate from './PreviewSendCertificate';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import '../../../styles/sendCertificate.css';
import '../../../styles/emailCheck.css';

type EmailCheckStatus =
  | 'idle'
  | 'loading'
  | 'connected'
  | 'not-connected'
  | 'connecting'
  | 'error';

type EmailIntegration = {
  id: number;
  provider: string;
  email_address: string;
  status: string;
  default: boolean;
  centralized: boolean;
  connected_from_app: string;
  last_used_at: string;
  created_at: string;
  sharing_type: string;
};

const API_BASE = process.env.REACT_APP_COIPortal_Url;

function emailAuthHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
    NCToken: localStorage.getItem('NCToken') || '',
    Accept: 'application/json',
  };
}

function formatIntegrationDate(iso: string) {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return iso;
  }
}

export type Step7Props = {
  email: string;
  contactName: string;
  companyName: string;
  sendToEmail: boolean;
  sendToInsuredEmail: boolean;
  copyToInsuredEmail: string[];
  fax?: string;
  sendToFax: boolean;
  insuredFax?: string;
  sendToInsuredFax: boolean;
  ccToMe: boolean;
  insuredDatabaseId?: string;
  selectedMasterCertificates: string[];
  selectedCertificates: { id: string; title: string }[];
  selectedVehicles: string[];
  selectedDrivers: string[];
  selectedEquipment: string[];
  selectedProperties: string[];
  certificateHolder: { label: string; value: string }[];
  descText: string;
  showDescriptionInAcord101?: boolean;
  message: string;
  setMessage: (v: string) => void;
  onSend: (scheduledDate?: Date) => void;
  isSending?: boolean;
  fileDatabaseId?: string | null;
};

const Step7: React.FC<Step7Props> = ({
  email,
  contactName,
  companyName,
  sendToEmail,
  sendToInsuredEmail,
  copyToInsuredEmail,
  fax,
  sendToFax,
  insuredFax,
  sendToInsuredFax,
  ccToMe,
  insuredDatabaseId,
  selectedMasterCertificates,
  selectedCertificates,
  selectedVehicles,
  selectedDrivers,
  selectedEquipment,
  selectedProperties,
  certificateHolder,
  descText,
  showDescriptionInAcord101,
  message,
  setMessage,
  onSend,
  isSending,
  fileDatabaseId,
}) => {
  const [previewVisible, setPreviewVisible] = React.useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = React.useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = React.useState(false);
  const [scheduleDialogVisible, setScheduleDialogVisible] = React.useState(false);

  const [emailModalVisible, setEmailModalVisible] = React.useState(false);
  const [emailCheckStatus, setEmailCheckStatus] = React.useState<EmailCheckStatus>('idle');
  const [emailCheckError, setEmailCheckError] = React.useState('');
  const [emailIntegration, setEmailIntegration] = React.useState<EmailIntegration | null>(null);
  const waitingForOAuth = React.useRef(false);
  const autoConnectFired = React.useRef(false);

  const checkEmailIntegration = React.useCallback(async () => {
    setEmailCheckStatus('loading');
    setEmailCheckError('');
    setEmailIntegration(null);
    try {
      const res = await axios.get(
        `${API_BASE}/api/igigcoi/checkEmailIntegration`,
        { headers: emailAuthHeaders() }
      );
      const data = res.data;
      if (data?.has_integration && data?.integration) {
      //if (true) {
        waitingForOAuth.current = false;
        setEmailIntegration(data.integration);
        setEmailCheckStatus('connected');
      } else {
        setEmailCheckStatus('not-connected');
        setEmailModalVisible(true);
      }
    } catch (err: any) {
      setEmailCheckStatus('error');
      setEmailCheckError(err?.response?.data?.message || 'Failed to check email integration status.');
      setEmailModalVisible(true);
    }
  }, []);

  const handleConnectEmail = React.useCallback(async () => {
    setEmailCheckStatus('connecting');
    setEmailCheckError('');
    try {
      const res = await axios.get(
        `${API_BASE}/api/igigcoi/emailExchangeLoginUrl`,
        { headers: emailAuthHeaders() }
      );
      const data = res.data;
      const redirectUrl: string = data?.url ?? data?.oauth_url ?? '';
      const codeVerifier: string = data?.code_verifier ?? '';
      if (!redirectUrl) {
        setEmailCheckStatus('error');
        setEmailCheckError('No authorization URL returned from server.');
        return;
      }
      if (codeVerifier) {
        sessionStorage.setItem('email_exchange_code_verifier', codeVerifier);
      }
      window.open(redirectUrl, '_blank', 'noopener,noreferrer');
      waitingForOAuth.current = true;
      setEmailCheckStatus('not-connected');
    } catch (err: any) {
      setEmailCheckStatus('error');
      setEmailCheckError(err?.response?.data?.message || 'Failed to get Exchange login URL.');
    }
  }, []);

  React.useEffect(() => {
    checkEmailIntegration();
  }, [checkEmailIntegration]);

  React.useEffect(() => {
    if (emailModalVisible && !autoConnectFired.current) {
      autoConnectFired.current = true;
      handleConnectEmail();
    }
  }, [emailModalVisible, handleConnectEmail]);

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && waitingForOAuth.current) {
        checkEmailIntegration();
      }
    };
    const handleWindowFocus = () => {
      if (waitingForOAuth.current) {
        checkEmailIntegration();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [checkEmailIntegration]);
  const [scheduleDate, setScheduleDate] = React.useState<Date | null>(null);
  const [scheduleType, setScheduleType] = React.useState<'add' | 'schedule' | null>(null);
  const [schedPickedDay, setSchedPickedDay] = React.useState<Date | null>(null);
  const [schedHour, setSchedHour] = React.useState('12');
  const [schedMinute, setSchedMinute] = React.useState('00');
  const [schedSecond, setSchedSecond] = React.useState('00');
  const [schedAmPm, setSchedAmPm] = React.useState<'AM' | 'PM'>('PM');

  const buildScheduleDate = React.useCallback((day: Date | null, h: string, m: string, s: string, ampm: 'AM' | 'PM') => {
    if (!day) return null;
    let hour = parseInt(h, 10) || 0;
    const minute = parseInt(m, 10) || 0;
    const second = parseInt(s, 10) || 0;
    if (ampm === 'AM') { if (hour === 12) hour = 0; }
    else { if (hour !== 12) hour += 12; }
    const d = new Date(day);
    d.setHours(hour, minute, second, 0);
    return d;
  }, []);

  const handleTimeInput = (setter: React.Dispatch<React.SetStateAction<string>>, max: number) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
      setter(raw);
    };

  const handleTimeBlur = (value: string, setter: React.Dispatch<React.SetStateAction<string>>, max: number, min: number = 0) => {
    let n = parseInt(value, 10);
    if (isNaN(n)) n = min;
    if (n < min) n = min;
    if (n > max) n = max;
    setter(String(n).padStart(2, '0'));
  };

  const openScheduleDialog = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);
    setSchedPickedDay(now);
    let h = now.getHours();
    const ampm = h >= 12 ? 'PM' : 'AM';
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    setSchedHour(String(h).padStart(2, '0'));
    setSchedMinute(String(now.getMinutes()).padStart(2, '0'));
    setSchedSecond('00');
    setSchedAmPm(ampm);
    setScheduleType('schedule');
    setScheduleDialogVisible(true);
  };
  const toastRef = React.useRef<Toast>(null);

  const handlePreviewCertificate = async () => {
    setIsLoadingPdf(true);
    setPreviewVisible(true);
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      const certificateId = selectedMasterCertificates[0];
      const payload: any = {
        insuredDatabaseId: insuredDatabaseId || '',
        certificateId,
        certificateHolder: certificateHolder.map(h => ({ id: h.value })),
        additionalInsuredId: (() => {
          for (const h of certificateHolder) {
            const raw = (h as any)._raw;
            const match = raw?.additionalInsuredDetails?.find(
              (ai: any) => ai.truckingCompanyId === insuredDatabaseId
            );
            if (match) return match.dbId;
          }
          return null;
        })(),
        vehicleIds: selectedVehicles.map(id => ({ id })),
        driverIds: selectedDrivers.map(id => ({ id })),
        equipmentIds: selectedEquipment.map(id => ({ id })),
        propertyIds: selectedProperties.map(id => ({ id })),
        description: descText,
        showDescriptionInAcord101: showDescriptionInAcord101 ?? true,
      };
      const response = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/preview-certificate`, {
        method: 'POST',
        headers: {
          'NCToken': ncToken,
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to fetch preview PDF');
      const data = await response.json();
      if (data && data.success && data.previewUrl) {
        setPdfBlobUrl(data.previewUrl);
      } else {
        setPdfBlobUrl(null);
      }
    } catch (error) {
      setPdfBlobUrl(null);
    } finally {
      setIsLoadingPdf(false);
    }
  };

  const handleHidePreview = () => {
    setPreviewVisible(false);
    setPdfBlobUrl(null);
    setIsLoadingPdf(false);
  };

  return (
    <div className="page">
      <Toast ref={toastRef} />
      <div className="page__sectionHeader">
        <div>
          <h2 className="page__title">Review & Send</h2>
          <p className="page__muted">Review details before sending the certificate</p>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', marginBottom: 12, borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
        <i className="pi pi-info-circle" style={{ color: '#2563eb', fontSize: 14, marginTop: 2, flexShrink: 0 }} />
        <span style={{ fontSize: 12, color: '#1e40af', lineHeight: 1.5 }}>
          Reminder: The email address used to send this message will be the farmersagent.com or farmersagency.com email address associated with the user ID you used to access the portal through FBIE&reg; or APEX.
        </span>
      </div>
      <div className="page__twoCol" style={{ gridTemplateColumns: '1fr' }}>
        <div className="pageCard" style={{ padding: 0, overflow: 'hidden' }}>

          {/* Card header */}
          <div style={{
            padding: '14px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            borderBottom: '1px solid #f1f5f9',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="pi pi-check-circle" style={{ fontSize: 15, color: '#16a34a' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>Ready to send</span>
            </div>
            <Button
              label="Preview Certificate"
              icon="pi pi-eye"
              onClick={handlePreviewCertificate}
              loading={isLoadingPdf}
              disabled={isLoadingPdf || !!isSending}
              style={{
                border: '1px solid #d32f2f',
                background: 'transparent',
                color: '#d32f2f',
                fontWeight: 600,
                borderRadius: '6px',
                padding: '0.35rem 1rem',
                fontSize: 12,
                boxShadow: 'none',
              }}
            />
          </div>

          {/* Delivery summary */}
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sendToEmail && (
              email?.trim()
                ? <DeliveryRow icon="pi-envelope" color="#0057A4" bg="#eff6ff" border="#bfdbfe" label="Recipient" value={email} sub={companyName} />
                : <DeliveryRow icon="pi-envelope" color="#d97706" bg="#fffbeb" border="#fde68a" label="Recipient" value="No email address entered" />
            )}
            {sendToInsuredEmail && copyToInsuredEmail.filter(Boolean).map((e, i) => (
              <DeliveryRow key={i} icon="pi-envelope" color="#0057A4" bg="#eff6ff" border="#bfdbfe" label="CC — Insured" value={e} />
            ))}
            {ccToMe && (
              <DeliveryRow icon="pi-user" color="#6366f1" bg="#eef2ff" border="#c7d2fe" label="CC — Me" value={localStorage.getItem('workEmail') || localStorage.getItem('userName') || 'You'} />
            )}
            {sendToFax && fax?.trim() && (
              <DeliveryRow icon="pi-print" color="#0891b2" bg="#ecfeff" border="#a5f3fc" label="Fax" value={fax} />
            )}
            {sendToInsuredFax && insuredFax?.trim() && (
              <DeliveryRow icon="pi-print" color="#0891b2" bg="#ecfeff" border="#a5f3fc" label="Fax — Insured" value={insuredFax} />
            )}
          </div>

          {/* Certificates */}
          {selectedCertificates.length > 0 && (
            <div style={{ padding: '0 20px 16px', paddingLeft: 74 }}>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Certificates</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selectedCertificates.map((c) => (
                  <span key={c.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                    borderRadius: 20, padding: '4px 10px', fontSize: 12, color: '#334155', fontWeight: 500,
                  }}>
                    <i className="pi pi-file-check" style={{ fontSize: 11, color: '#0057A4' }} />
                    {c.title}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Compliance notice */}
          <div style={{
            margin: '0 20px 16px',
            padding: '14px 16px',
            borderRadius: 8,
            background: '#fff7ed',
            border: '1.5px solid #fb923c',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}>
            <i className="pi pi-exclamation-triangle" style={{ color: '#c2410c', fontSize: 16, marginTop: 2, flexShrink: 0 }} />
            <span style={{ fontSize: 12.5, color: '#7c2d12', lineHeight: 1.6, fontWeight: 500 }}>
              By clicking <strong>Schedule Certificate</strong> or <strong>Send Certificate</strong>, I confirm that I have read and will only send email material that follows the{' '}
              <a
                href="https://csstg.farmers.com/content/dam/apex/documents/non-srn/compliance/Compliance-Brand-Guidelines-Reminders.pdf"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#c2410c', fontWeight: 700, textDecoration: 'underline' }}
              >
                brand guidelines and compliance reminders
              </a>. Please see our{' '}
              <a
                href="https://agencynews.farmers.com/help-and-support/tech-resources-texting-outlook/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#c2410c', fontWeight: 700, textDecoration: 'underline' }}
              >
                Outlook and texting resources
              </a>{' '}
              for more information.
            </span>
          </div>

          {/* Action footer */}
          {(emailCheckStatus === 'idle' || emailCheckStatus === 'loading') && (
            <div style={{ borderTop: '1px solid #f1f5f9', padding: '14px 20px', display: 'flex', justifyContent: 'flex-end', background: '#fafbfc' }}>
              <ProgressSpinner style={{ width: 28, height: 28 }} strokeWidth="4" />
            </div>
          )}
          <div style={{ borderTop: '1px solid #f1f5f9', padding: '14px 20px', display: (emailCheckStatus === 'idle' || emailCheckStatus === 'loading') ? 'none' : 'flex', justifyContent: 'flex-end', gap: 8, background: '#fafbfc' }}>
            <Button
              label={scheduleDate ? 'Reschedule Certificate' : 'Schedule Certificate'}
              icon="pi pi-clock"
              className="appButton--primary"
              onClick={openScheduleDialog}
              loading={!!isSending && !!scheduleDate}
              disabled={!!isSending}
            />
            <Button
              label="Send Certificate"
              icon="pi pi-send"
              className="appButton--primary"
              onClick={() => onSend()}
              loading={!!isSending}
              disabled={!!isSending}
            />
          </div>

        </div>
      </div>
      <PreviewSendCertificate
        visible={previewVisible}
        onHide={handleHidePreview}
        pdfBlobUrl={pdfBlobUrl}
        isLoadingPdf={isLoadingPdf}
      />

      {/* Email integration check modal */}
      <Dialog
        visible={emailModalVisible}
        onHide={() => { if (emailCheckStatus === 'connected') setEmailModalVisible(false); }}
        closable={emailCheckStatus === 'connected'}
        closeOnEscape={emailCheckStatus === 'connected'}
        header={null}
        footer={null}
        style={{ width: '500px', maxWidth: '95vw' }}
        modal
        draggable={false}
        resizable={false}
        contentStyle={{ padding: 0 }}
      >
        <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
          {/* Header */}
          <div className="emailcheck__header">
            <div className="emailcheck__headerIcon">
              <i className="pi pi-envelope" />
            </div>
            <div>
              <h1 className="emailcheck__title">Email Integration</h1>
              <p className="emailcheck__subtitle">Connect your email account to enable sending certificates directly</p>
            </div>
          </div>
          <div className="emailcheck__divider" />

          {/* Body */}
          <div className="emailcheck__body">

            {(emailCheckStatus === 'idle' || emailCheckStatus === 'loading') && (
              <div className="emailcheck__stateWrap">
                <div className="emailcheck__spinnerWrap">
                  <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
                <p className="emailcheck__stateTitle">Checking connection status...</p>
                <p className="emailcheck__stateDesc">Please wait while we verify your email integration.</p>
              </div>
            )}

            {emailCheckStatus === 'connecting' && (
              <div className="emailcheck__stateWrap">
                <div className="emailcheck__spinnerWrap">
                  <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
                <p className="emailcheck__stateTitle">Opening authorization window...</p>
                <p className="emailcheck__stateDesc">Please complete sign-in in the new tab, then re-check status here.</p>
              </div>
            )}

            {emailCheckStatus === 'connected' && emailIntegration && (
              <div className="emailcheck__connectedWrap">
                <div className="emailcheck__statusIcon emailcheck__statusIcon--success">
                  <i className="pi pi-check" />
                </div>
                <p className="emailcheck__stateTitle emailcheck__stateTitle--success">Email Connected</p>
                <p className="emailcheck__stateDesc">Your email integration is active and ready to use.</p>

                <div className="emailcheck__detailCard">
                  <div className="emailcheck__detailRow">
                    <span className="emailcheck__detailLabel"><i className="pi pi-at" /> Email Address</span>
                    <span className="emailcheck__detailValue">{emailIntegration.email_address}</span>
                  </div>
                  <div className="emailcheck__detailRow">
                    <span className="emailcheck__detailLabel"><i className="pi pi-server" /> Provider</span>
                    <span className="emailcheck__detailValue emailcheck__capitalize">{emailIntegration.provider}</span>
                  </div>
                  <div className="emailcheck__detailRow">
                    <span className="emailcheck__detailLabel"><i className="pi pi-info-circle" /> Status</span>
                    <span className={`emailcheck__badge emailcheck__badge--${emailIntegration.status}`}>
                      {emailIntegration.status}
                    </span>
                  </div>
                  {emailIntegration.last_used_at && (
                    <div className="emailcheck__detailRow">
                      <span className="emailcheck__detailLabel"><i className="pi pi-clock" /> Last Used</span>
                      <span className="emailcheck__detailValue">{formatIntegrationDate(emailIntegration.last_used_at)}</span>
                    </div>
                  )}
                  <div className="emailcheck__detailRow">
                    <span className="emailcheck__detailLabel"><i className="pi pi-calendar" /> Connected On</span>
                    <span className="emailcheck__detailValue">{formatIntegrationDate(emailIntegration.created_at)}</span>
                  </div>
                </div>

                <div className="emailcheck__actionRow">
                  <Button
                    label="Re-check Status"
                    icon="pi pi-refresh"
                    className="p-button-outlined emailcheck__retryBtn"
                    onClick={checkEmailIntegration}
                  />
                  <Button
                    label="Continue"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    className="emailcheck__connectBtn"
                    onClick={() => setEmailModalVisible(false)}
                  />
                </div>
              </div>
            )}

            {emailCheckStatus === 'not-connected' && (
              <div className="emailcheck__stateWrap">
                <div className="emailcheck__statusIcon emailcheck__statusIcon--warning">
                  <i className="pi pi-exclamation-triangle" />
                </div>
                <p className="emailcheck__stateTitle emailcheck__stateTitle--warning">Email Not Connected</p>
                <p className="emailcheck__stateDesc">
                  Your email is not connected yet. Connect your Microsoft Exchange account to start sending certificates directly from your inbox.
                </p>
                <div className="emailcheck__actionRow">
                  <Button
                    label="Check Again"
                    icon="pi pi-refresh"
                    className="p-button-outlined emailcheck__retryBtn"
                    onClick={checkEmailIntegration}
                  />
                  <Button
                    label="Connect Email"
                    icon="pi pi-envelope"
                    className="emailcheck__connectBtn"
                    onClick={handleConnectEmail}
                  />
                </div>
              </div>
            )}

            {emailCheckStatus === 'error' && (
              <div className="emailcheck__stateWrap">
                <div className="emailcheck__statusIcon emailcheck__statusIcon--error">
                  <i className="pi pi-times" />
                </div>
                <p className="emailcheck__stateTitle emailcheck__stateTitle--error">Connection Error</p>
                {emailCheckError && (
                  <p className="emailcheck__stateDesc emailcheck__stateDesc--error">{emailCheckError}</p>
                )}
                <Button
                  label="Try Again"
                  icon="pi pi-refresh"
                  className="p-button-outlined emailcheck__retryBtn"
                  onClick={checkEmailIntegration}
                />
              </div>
            )}

          </div>
        </div>
      </Dialog>
      <Dialog
        visible={scheduleDialogVisible}
        onHide={() => setScheduleDialogVisible(false)}
        header={null}
        footer={null}
        className="schedDlg"
        modal
        draggable={false}
        resizable={false}
      >
        {/* Icon */}
        <div className="schedDlg__iconWrap">
          <i className="pi pi-clock schedDlg__dlgIcon" />
        </div>
        <div className="schedDlg__title">Schedule Certificate</div>
        <div className="schedDlg__subtitle">
          Select a date and time to automatically send this certificate.
        </div>

        <div className="schedDlg__body">
          <div>
            <label className="schedDlg__fieldLabel">Date</label>
            <div className="schedDlg__dateCell">
              <Calendar
                value={schedPickedDay}
                onChange={e => setSchedPickedDay(e.value as Date)}
                minDate={new Date()}
                placeholder="Select date"
                showIcon
                dateFormat="mm/dd/yy"
              />
            </div>
          </div>
          <div>
            <label className="schedDlg__fieldLabel">Time</label>
            <div className="schedDlg__timePill">
              <input type="text" value={schedHour} onChange={handleTimeInput(setSchedHour, 12)} onBlur={() => handleTimeBlur(schedHour, setSchedHour, 12, 1)} maxLength={2} placeholder="HH" className="schedDlg__timeInput" />
              <span className="schedDlg__timeSep">:</span>
              <input type="text" value={schedMinute} onChange={handleTimeInput(setSchedMinute, 59)} onBlur={() => handleTimeBlur(schedMinute, setSchedMinute, 59, 0)} maxLength={2} placeholder="MM" className="schedDlg__timeInput" />
              <span className="schedDlg__timeSep">:</span>
              <input type="text" value={schedSecond} onChange={handleTimeInput(setSchedSecond, 59)} onBlur={() => handleTimeBlur(schedSecond, setSchedSecond, 59, 0)} maxLength={2} placeholder="SS" className="schedDlg__timeInput" />
              <div className="schedDlg__ampmWrap">
                {(['AM', 'PM'] as const).map(ap => (
                  <button key={ap} type="button" onClick={() => setSchedAmPm(ap)} className={`schedDlg__ampmBtn${schedAmPm === ap ? ' schedDlg__ampmBtn--active' : ''}`}>{ap}</button>
                ))}
              </div>
            </div>
            <div className="schedDlg__hint">Click any field to type hours, minutes, or seconds directly.</div>
          </div>
        </div>

        {/* Footer */}
        <div className="schedDlg__footer">
          <button type="button" className="p-button schedDlg__cancelBtn" onClick={() => setScheduleDialogVisible(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="p-button schedDlg__confirmBtn"
            disabled={!schedPickedDay}
            onClick={() => {
              const built = buildScheduleDate(schedPickedDay, schedHour, schedMinute, schedSecond, schedAmPm);
              if (!built || built.getTime() <= Date.now()) {
                toastRef.current?.show({
                  severity: 'error',
                  summary: 'Invalid Schedule',
                  detail: 'Please select a future date and time.',
                  life: 3000,
                });
                return;
              }
              setScheduleDate(built);
              setScheduleDialogVisible(false);
              onSend(built ?? undefined);
              setScheduleType(null);
            }}
          >
            <i className="pi pi-check" style={{ fontSize: 12 }} />
            Schedule
          </button>
        </div>
      </Dialog>
    </div>
  );
};

type DeliveryRowProps = { icon: string; color: string; bg: string; border: string; label: string; value: string; sub?: string };
const DeliveryRow: React.FC<DeliveryRowProps> = ({ icon, color, bg, border, label, value, sub }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
    <div style={{
      width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
      background: bg, border: `1px solid ${border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <i className={`pi ${icon}`} style={{ fontSize: 14, color }} />
    </div>
    <div style={{ minWidth: 0, paddingTop: 2 }}>
      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#334155', wordBreak: 'break-all', lineHeight: 1.4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>{sub}</div>}
    </div>
  </div>
);

export default Step7;
